import type { Translations } from "@/i18n";
import type { TripHistory, Vehicle, VehicleGroup } from "@/types/fleet";
import type { ClientDriver } from "@/lib/api/drivers";
import {
  consumptionPer100Km,
  formatConsumption,
  formatMetric,
  NO_VALUE,
} from "@/lib/format/metric";
import {
  formatDriverDisplaysText,
  formatDriverRef,
  resolveDriverDisplays,
  resolveRefDisplay,
  type DriverRef,
} from "@/lib/fleet/driver-ref";
import {
  collectRouteAttrColumns,
  flattenRouteAttrs,
  isFuelLevelAttrTitle,
  rawRouteAttrValues,
  summarizeRouteAttrs,
} from "@/lib/history/route-attr-columns";
import {
  aggregateTripsByDriver,
  aggregateTripsByVehicle,
  collectDriverRefs,
} from "@/lib/history/aggregate-trip-report";
import {
  toExcelCell,
  withUnit,
  type ExcelCellInput,
  type ExportCellSpec,
} from "@/lib/export/excel";
import type { PdfExportField } from "@/lib/export/pdf";
import {
  ALL_FEATURES_ON,
  type ClientFeature,
  type ClientFeatures,
} from "@/lib/fleet/client-features";

/**
 * Everything a report is, independent of where it is shown. `views/Reports.tsx` renders it as a
 * table and exports it on demand; the scheduled-report renderer
 * (`app/api/internal/scheduled-reports/render`) builds the same rows on the server and attaches the
 * PDF/Excel to an email. Keeping one implementation is what lets an emailed report match the screen.
 *
 * Nothing here may touch React, the DOM or the language context — translations and date formatting
 * come in as arguments.
 */

export const REPORT_TYPES = ["trips", "fuel", "drivers", "vehicles"] as const;

export type ReportType = (typeof REPORT_TYPES)[number];

export function isReportType(value: unknown): value is ReportType {
  return typeof value === "string" && (REPORT_TYPES as readonly string[]).includes(value);
}

/** The client feature a whole report type needs; types not listed are always available. */
const REPORT_TYPE_FEATURE: Partial<Record<ReportType, ClientFeature>> = { fuel: "fuel" };

/** The report types a client can run — the Fuel Report is meaningless without fuel data. */
export function availableReportTypes(features: ClientFeatures): ReportType[] {
  return REPORT_TYPES.filter((type) => {
    const feature = REPORT_TYPE_FEATURE[type];
    return !feature || features[feature];
  });
}

export function isReportTypeAvailable(type: ReportType, features: ClientFeatures): boolean {
  const feature = REPORT_TYPE_FEATURE[type];
  return !feature || features[feature];
}

export interface ReportColumn extends ExportCellSpec {
  key: string;
  label: string;
  /**
   * How the totals row folds this column. Omitted leaves the cell blank — the right answer for
   * identifiers and timestamps, which have no meaningful total.
   *
   * `avg` is a plain mean unless `weightKey` names a column to weight by; an unweighted mean of
   * per-vehicle average speeds would count a van that drove 2 km the same as one that drove 200.
   */
  aggregate?: "sum" | "avg" | "max";
  weightKey?: string;
  /** Overrides how the totals cell is written, for columns with their own decimal convention. */
  formatTotal?: (value: number) => string;
  /** Dropped by `reportConfigs` for a client without this feature (`lib/fleet/client-features.ts`). */
  feature?: ClientFeature;
}

/** One report row: display strings keyed by column, plus the hidden `EXCEL_RAW` twins. */
export type ReportRow = Record<string, unknown>;

export type ReportTotals = Record<string, { display: string; raw: number }>;

export interface ReportRange {
  from: Date;
  to: Date;
}

/** Date and duration formatting, supplied by the caller: the view's hooks, or the server's tz-aware ones. */
export interface ReportFormatters {
  formatDateTime: (date: Date) => string;
  formatDuration: (durationMinutes: number) => string;
}

/**
 * `reportData` rows are pre-formatted for the table and the PDF (`"29.1 km/h"`), which Excel can
 * only store as text. Rows therefore carry their unformatted values under this key; it is not a
 * column key, so neither the table nor the PDF can see it.
 */
const EXCEL_RAW = "__excelRaw" as const;

type ExcelRawValues = Record<string, number | string | undefined>;

/** Reads the unformatted value, falling back to the displayed one for columns without a twin. */
export function excelRawValue(row: unknown, key: string): unknown {
  const raw = (row as Record<string, unknown>)[EXCEL_RAW] as ExcelRawValues | undefined;
  // `hasOwnProperty`, not truthiness: an intentional blank is stored as `undefined`, and must
  // win over the formatted string rather than falling through to it.
  if (raw && Object.prototype.hasOwnProperty.call(raw, key)) return raw[key];
  return (row as Record<string, unknown>)[key];
}

/**
 * The route event types the Fuel Report is built from. Both are upstream flags collected by
 * `collectEventTypes` (`lib/api/routes.ts`) — nothing here is inferred from tank levels, so a
 * vehicle whose device never reports a refuelling simply produces no rows.
 *
 * `fuel_drain` is listed although upstream does not send it today: the moment it does, the routes
 * carrying it appear in this report with no change here.
 */
const FUEL_ROUTE_EVENT_TYPES = new Set(["refuel", "fuel_drain"]);

/**
 * Report types whose rows come from `/api/routes` rather than from the reference lists — the ones
 * that must wait for route data before they can render.
 */
export const ROUTE_BACKED_REPORTS = new Set<ReportType>(["trips", "fuel", "drivers", "vehicles"]);

/**
 * Report types that get the dynamic per-device attr columns appended (`collectRouteAttrColumns`).
 * The aggregating reports do; the Fuel Report does not — its rows are single events, and the attr
 * summaries are period sums that would read as a measurement of the refuelling itself.
 */
const ATTR_COLUMN_REPORTS = new Set<ReportType>(["trips", "drivers", "vehicles"]);

/**
 * Refuelling tallies for a bucket of routes, shared by the Fuel and Vehicle Utilization reports
 * so both count the same thing.
 *
 * Only `refuel` counts — `fuel_drain` is the opposite operation and folding it in would net out
 * to a figure that means nothing. Upstream quantifies the litres on some devices only, so an
 * unquantified fill still counts as an event and simply adds nothing to the total.
 */
function summarizeRefuellings(trips: readonly TripHistory[]) {
  let count = 0;
  let litres = 0;
  let lastAt: number | null = null;

  for (const trip of trips) {
    if (!(trip.eventTypes ?? []).includes("refuel")) continue;
    count += 1;

    const stamp = new Date(trip.startTime).getTime();
    if (Number.isFinite(stamp) && (lastAt === null || stamp > lastAt)) lastAt = stamp;

    const amount = trip.refuelledLitres;
    if (typeof amount === "number" && Number.isFinite(amount) && amount > 0) litres += amount;
  }

  return { count, litres: Math.round(litres * 100) / 100, lastAt };
}

export function createDurationFormatter(t: Translations) {
  return (durationMinutes: number) =>
    durationMinutes > 0
      ? `${Math.floor(durationMinutes / 60)}${t.common.hoursShort} ${durationMinutes % 60}${t.common.minutesShort}`
      : NO_VALUE;
}

export interface ReportConfig {
  title: string;
  description: string;
  columns: ReportColumn[];
}

/**
 * Every report's static columns, minus those the client's hardware cannot fill. A missing feature
 * hides its columns rather than showing a row of zeros; rows still carry the values, unread.
 */
export function reportConfigs(
  t: Translations,
  features: ClientFeatures = ALL_FEATURES_ON,
): Record<ReportType, ReportConfig> {
  const configs = allReportConfigs(t);
  if (features === ALL_FEATURES_ON) return configs;
  for (const config of Object.values(configs)) {
    config.columns = config.columns.filter((col) => !col.feature || features[col.feature]);
  }
  return configs;
}

function allReportConfigs(t: Translations): Record<ReportType, ReportConfig> {
  return {
    trips: {
      title: t.reportsPage.tripReport,
      description: t.reportsPage.tripReportDesc,
      columns: [
        { key: "deviceId", label: t.vehiclesPage.deviceId },
        { key: "plateNumber", label: t.vehiclesPage.plateNumber },
        { key: "vehicleName", label: t.historyPage.vehicle },
        { key: "drivers", label: t.tracking.driver },
        { key: "distanceTotal", label: t.reportsPage.totalDistanceKm, unit: t.common.kilometersShort, excel: "number", aggregate: "sum" },
        { key: "duration", label: t.reportsPage.totalDurationMinutes, excel: "duration", excelLabel: t.reportsPage.totalDurationHms, aggregate: "sum" },
        { key: "avgSpeed", label: t.historyPage.avgSpeed, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "avg", weightKey: "distanceTotal" },
        { key: "maxSpeed", label: t.historyPage.maxSpeed, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "max" },
        { key: "fuelTotal", label: t.reportsPage.fuelL, unit: t.common.litersShort, excel: "number", aggregate: "sum", feature: "fuel" },
        { key: "avgConsumption", label: t.reportsPage.avgConsumption, unit: t.common.litersPer100KmShort, excel: "number", aggregate: "avg", weightKey: "distanceTotal", formatTotal: (value) => `${value.toFixed(1)} ${t.common.litersShort}`, feature: "fuel" },
      ],
    },
    fuel: {
      title: t.reportsPage.fuelReport,
      description: t.reportsPage.fuelReportDesc,
      // One row per vehicle, summing its refuellings over the period. The per-event view lives in
      // History, which already shows the pump and the litres on the route the event fell on.
      columns: [
        { key: "deviceId", label: t.vehiclesPage.deviceId },
        { key: "plateNumber", label: t.vehiclesPage.plateNumber },
        { key: "vehicleName", label: t.historyPage.vehicle },
        { key: "drivers", label: t.tracking.driver },
        { key: "refuelCount", label: t.reportsPage.refuelCount, excel: "number", aggregate: "sum" },
        { key: "amount", label: t.reportsPage.refuelledTotalL, unit: t.common.litersShort, excel: "number", aggregate: "sum" },
        // The most recent one, so a vehicle that stopped refuelling mid-period is visible.
        { key: "lastRefuel", label: t.reportsPage.lastRefuel },
      ],
    },
    drivers: {
      title: t.reportsPage.driverReport,
      description: t.reportsPage.driverReportDesc,
      // The vehicle report's metrics, bucketed by the driver upstream reported on each route
      // instead of by the vehicle. `vehicleName` is therefore what they actually drove in the
      // period, not the assignment currently on the vehicle record.
      columns: [
        { key: "name", label: t.reportsPage.driver },
        { key: "vehicleName", label: t.historyPage.vehicle },
        { key: "totalTrips", label: t.reportsPage.totalTrips, excel: "number", aggregate: "sum" },
        { key: "totalDistance", label: t.reportsPage.totalDistanceKm, unit: t.common.kilometersShort, excel: "number", aggregate: "sum" },
        { key: "totalDuration", label: t.reportsPage.totalDurationMinutes, excel: "duration", excelLabel: t.reportsPage.totalDurationHms, aggregate: "sum" },
        { key: "avgSpeed", label: t.reportsPage.avgSpeedKmh, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "avg", weightKey: "totalDistance" },
        { key: "maxSpeed", label: t.historyPage.maxSpeed, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "max" },
        { key: "fuelTotal", label: t.reportsPage.fuelL, unit: t.common.litersShort, excel: "number", aggregate: "sum", feature: "fuel" },
        { key: "avgConsumption", label: t.reportsPage.avgConsumption, unit: t.common.litersPer100KmShort, excel: "number", aggregate: "avg", weightKey: "totalDistance", formatTotal: (value) => `${value.toFixed(1)} ${t.common.litersShort}`, feature: "fuel" },
      ],
    },
    vehicles: {
      title: t.reportsPage.vehicleUtilization,
      description: t.reportsPage.vehicleReportDesc,
      columns: [
        { key: "deviceId", label: t.vehiclesPage.deviceId },
        { key: "plateNumber", label: t.vehiclesPage.plateNumber },
        { key: "name", label: t.historyPage.vehicle },
        { key: "groupName", label: t.reportsPage.group },
        { key: "drivers", label: t.tracking.driver },
        { key: "totalTrips", label: t.reportsPage.totalTrips, excel: "number", aggregate: "sum" },
        { key: "totalDistance", label: t.reportsPage.totalDistanceKm, unit: t.common.kilometersShort, excel: "number", aggregate: "sum" },
        { key: "totalDuration", label: t.reportsPage.totalDurationMinutes, excel: "duration", excelLabel: t.reportsPage.totalDurationHms, aggregate: "sum" },
        { key: "avgSpeed", label: t.reportsPage.avgSpeedKmh, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "avg", weightKey: "totalDistance" },
        { key: "maxSpeed", label: t.historyPage.maxSpeed, unit: t.common.kilometersPerHourShort, excel: "number", aggregate: "max" },
        { key: "fuelTotal", label: t.reportsPage.fuelL, unit: t.common.litersShort, excel: "number", aggregate: "sum", feature: "fuel" },
        { key: "avgConsumption", label: t.reportsPage.avgConsumption, unit: t.common.litersPer100KmShort, excel: "number", aggregate: "avg", weightKey: "totalDistance", formatTotal: (value) => `${value.toFixed(1)} ${t.common.litersShort}`, feature: "fuel" },
        { key: "refuelCount", label: t.reportsPage.refuelCount, excel: "number", aggregate: "sum", feature: "fuel" },
        { key: "refuelledTotal", label: t.reportsPage.refuelledTotalL, unit: t.common.litersShort, excel: "number", aggregate: "sum", feature: "fuel" },
      ],
    },
  };
}

/**
 * The columns a report actually renders: its static ones, plus the per-device attr columns found in
 * the data, or the drain column once upstream starts sending `fuel_drain`.
 */
export function reportColumns(
  type: ReportType,
  baseColumns: ReportColumn[],
  trips: readonly TripHistory[],
  t: Translations,
): ReportColumn[] {
  if (type === "fuel") {
    // Upstream does not send `fuel_drain` today, so the drain column would be a row of zeros on
    // every fleet. It appears only once the data actually carries one.
    const hasFuelDrain = trips.some((trip) => trip.eventTypes?.includes("fuel_drain"));
    return hasFuelDrain
      ? [
          ...baseColumns,
          { key: "drainCount", label: t.reportsPage.drainCount, excel: "number", aggregate: "sum" },
        ]
      : baseColumns;
  }
  if (!ATTR_COLUMN_REPORTS.has(type)) return baseColumns;

  const attrColumns = collectRouteAttrColumns([...trips]).filter(
    (col) => !isFuelLevelAttrTitle(col.title),
  );
  if (attrColumns.length === 0) return baseColumns;

  return [
    ...baseColumns,
    ...attrColumns.map(
      (col): ReportColumn => ({
        key: col.key,
        label: col.title,
        unit: col.unit,
        excel: "number",
        aggregate: "sum",
      }),
    ),
  ];
}

export interface ReportRowsInput {
  type: ReportType;
  /** Already passed through `canonicalizeTripDriverRefs`. */
  trips: readonly TripHistory[];
  vehicles: readonly Vehicle[];
  drivers: readonly ClientDriver[];
  driverNamesById: Map<string, string>;
  range: ReportRange;
  /** `"all"` or a group id. Only the Vehicle Utilization report reads it; the rest arrive pre-filtered. */
  groupFilter: string;
  /** `"all"` or a vehicle id. */
  vehicleFilter: string;
}

export function buildReportRows(
  input: ReportRowsInput,
  t: Translations,
  fmt: ReportFormatters,
): ReportRow[] {
  const { trips, vehicles, drivers, driverNamesById, groupFilter, vehicleFilter } = input;
  const { from: fromDate, to: toDate } = input.range;
  const { formatDateTime, formatDuration } = fmt;

  switch (input.type) {
    case "trips": {
      let data = trips.filter((trip) => {
        const tripDate = new Date(trip.startTime);
        return tripDate >= fromDate && tripDate <= toDate;
      });
      if (vehicleFilter !== "all") {
        data = data.filter((trip) => trip.vehicleId === vehicleFilter);
      }

      return aggregateTripsByVehicle(data).map((row) => {
        const formatted = {
          ...row,
          // Plain text for the table and the PDF, which read String(row[col.key]) with no access
          // to any render function — a JSX-only column would render as a blank cell there.
          // Excel reads the unformatted twin below instead.
          drivers: formatDriverDisplaysText(
            resolveDriverDisplays(row.driverRefs, driverNamesById),
          ),
          distanceTotal: formatMetric(row.distanceTotal, t.common.kilometersShort),
          duration: formatDuration(row.duration),
          avgSpeed: formatMetric(row.avgSpeed, t.common.kilometersPerHourShort),
          maxSpeed: formatMetric(row.maxSpeed, t.common.kilometersPerHourShort),
          fuelTotal: formatMetric(row.fuelTotal, t.common.litersShort),
          avgConsumption: formatConsumption(
            row.fuelTotal,
            row.distanceTotal,
            t.common.litersShort,
          ),
        };
        const raw: ExcelRawValues = {
          distanceTotal: row.distanceTotal,
          duration: row.durationSeconds,
          avgSpeed: row.avgSpeed,
          maxSpeed: row.maxSpeed,
          fuelTotal: row.fuelTotal > 0 ? row.fuelTotal : undefined,
          avgConsumption: consumptionPer100Km(row.fuelTotal, row.distanceTotal),
          ...rawRouteAttrValues(row),
        };
        // Attached after flattenRouteAttrs, which would otherwise overwrite the numeric attrs.
        return {
          ...flattenRouteAttrs(formatted as TripHistory & typeof formatted),
          [EXCEL_RAW]: raw,
        };
      });
    }
    case "fuel": {
      let data = trips.filter((trip) => {
        const tripDate = new Date(trip.startTime);
        if (tripDate < fromDate || tripDate > toDate) return false;
        return trip.eventTypes?.some((type) => FUEL_ROUTE_EVENT_TYPES.has(type)) ?? false;
      });
      if (vehicleFilter !== "all") {
        data = data.filter((trip) => trip.vehicleId === vehicleFilter);
      }

      // Bucketed by vehicle: one row per car with its refuellings summed. The per-event view
      // lives in History, which shows the pump and the litres on the route the event fell on.
      const byVehicle = new Map<string, TripHistory[]>();
      for (const trip of data) {
        const bucket = byVehicle.get(trip.vehicleId);
        if (bucket) bucket.push(trip);
        else byVehicle.set(trip.vehicleId, [trip]);
      }

      return [...byVehicle.entries()]
        .map(([vehicleId, vehicleTrips]) => {
          const { count, litres, lastAt } = summarizeRefuellings(vehicleTrips);
          const drainCount = vehicleTrips.filter((trip) =>
            (trip.eventTypes ?? []).includes("fuel_drain"),
          ).length;
          const driverRefs = collectDriverRefs(vehicleTrips);
          const first = vehicleTrips[0];

          return {
            id: vehicleId,
            deviceId: first.deviceId ?? NO_VALUE,
            plateNumber: first.plateNumber,
            vehicleName: first.vehicleName,
            // Read by the table's `drivers` cell, which renders labels rather than text.
            driverRefs,
            drivers: formatDriverDisplaysText(resolveDriverDisplays(driverRefs, driverNamesById)),
            refuelCount: count,
            drainCount,
            amount: litres > 0 ? formatMetric(litres, t.common.litersShort) : NO_VALUE,
            lastRefuel: lastAt === null ? NO_VALUE : formatDateTime(new Date(lastAt)),
            litresValue: litres,
            [EXCEL_RAW]: {
              refuelCount: count,
              drainCount,
              amount: litres > 0 ? litres : undefined,
            } as ExcelRawValues,
          };
        })
        // Most fuel taken on first; the rest fall in by name rather than by map order.
        .sort(
          (a, b) =>
            b.litresValue - a.litresValue ||
            String(a.vehicleName).localeCompare(String(b.vehicleName)),
        );
    }
    case "drivers": {
      let rangeTrips = trips.filter((trip) => {
        const tripDate = new Date(trip.startTime);
        return tripDate >= fromDate && tripDate <= toDate;
      });
      if (vehicleFilter !== "all") {
        rangeTrips = rangeTrips.filter((trip) => trip.vehicleId === vehicleFilter);
      }

      const aggregated = aggregateTripsByDriver(rangeTrips);
      const byRefKey = new Map(aggregated.map((row) => [row.id, row]));

      // Every driver on the account, then any reference seen on a route that has no record
      // behind it — an RFID code, or an id pointing at a deleted driver. Without the second
      // group that driving would go unreported; without the first, a driver who did not drive
      // in the period would silently vanish rather than reading as zero.
      const refs: DriverRef[] = drivers.map((driver) => ({
        kind: "driver",
        driverId: driver.id,
      }));
      const listedKeys = new Set(refs.map(formatDriverRef));
      for (const row of aggregated) {
        if (!listedKeys.has(row.id)) refs.push(row.driverRef);
      }

      return refs
        .map((ref) => {
          const key = formatDriverRef(ref);
          const aggregate = byRefKey.get(key);
          const display = resolveRefDisplay(ref, driverNamesById);
          const metrics = {
            totalTrips: aggregate?.tripCount ?? 0,
            totalDistance: aggregate?.distanceTotal ?? 0,
            totalDuration: aggregate?.duration ?? 0,
            totalDurationSeconds: aggregate?.durationSeconds ?? 0,
            avgSpeed: aggregate?.avgSpeed ?? 0,
            maxSpeed: aggregate?.maxSpeed ?? 0,
            fuelTotal: aggregate?.fuelTotal ?? 0,
          };

          const row = {
            id: key,
            name: display.text || NO_VALUE,
            vehicleName:
              aggregate && aggregate.vehicles.length > 0
                ? aggregate.vehicles.map((vehicle) => vehicle.vehicleName).join(", ")
                : NO_VALUE,
            ...metrics,
            attrs: aggregate?.attrs,
          };
          const formatted = {
            ...row,
            totalDistance: formatMetric(metrics.totalDistance, t.common.kilometersShort),
            totalDuration: formatDuration(metrics.totalDuration),
            avgSpeed: formatMetric(metrics.avgSpeed, t.common.kilometersPerHourShort),
            maxSpeed: formatMetric(metrics.maxSpeed, t.common.kilometersPerHourShort),
            fuelTotal: formatMetric(metrics.fuelTotal, t.common.litersShort),
            avgConsumption: formatConsumption(
              metrics.fuelTotal,
              metrics.totalDistance,
              t.common.litersShort,
            ),
          };
          const raw: ExcelRawValues = {
            totalDistance: metrics.totalDistance,
            totalDuration: metrics.totalDurationSeconds,
            avgSpeed: metrics.avgSpeed,
            maxSpeed: metrics.maxSpeed,
            fuelTotal: metrics.fuelTotal > 0 ? metrics.fuelTotal : undefined,
            avgConsumption: consumptionPer100Km(metrics.fuelTotal, metrics.totalDistance),
            ...rawRouteAttrValues(row),
          };
          return {
            ...flattenRouteAttrs(formatted as TripHistory & typeof formatted),
            // Kept off the column list, so it sorts below without reaching the table or exports.
            distanceValue: metrics.totalDistance,
            [EXCEL_RAW]: raw,
          };
        })
        // Whoever drove most first; the drivers with nothing in the period fall to the bottom
        // in name order rather than diluting the top of the report.
        .sort(
          (a, b) =>
            b.distanceValue - a.distanceValue ||
            String(a.name).localeCompare(String(b.name)),
        );
    }
    case "vehicles": {
      let data = vehicles;
      if (groupFilter !== "all") {
        data = data.filter((vehicle) => vehicle.groupId === groupFilter);
      }
      if (vehicleFilter !== "all") {
        data = data.filter((vehicle) => vehicle.id === vehicleFilter);
      }

      const tripInRange = (trip: TripHistory) => {
        const tripDate = new Date(trip.startTime);
        return tripDate >= fromDate && tripDate <= toDate;
      };

      return data.map((vehicle) => {
        const vehicleTrips = trips.filter((trip) => trip.vehicleId === vehicle.id && tripInRange(trip));

        const totalTrips = vehicleTrips.length;
        const totalDistance = Math.round(vehicleTrips.reduce((sum, item) => sum + item.distanceTotal, 0) * 100) / 100;
        const totalDuration = vehicleTrips.reduce((sum, item) => sum + item.duration, 0);
        // Exact seconds for the Excel duration cell; the displayed total stays rounded minutes.
        const totalDurationSeconds = vehicleTrips.reduce(
          (sum, item) => sum + (item.durationSeconds ?? item.duration * 60),
          0,
        );
        const distSum = vehicleTrips.reduce((sum, item) => sum + item.distanceTotal, 0);
        const avgSpeed =
          distSum > 0
            ? Math.round((vehicleTrips.reduce((sum, item) => sum + item.distanceTotal * item.avgSpeed, 0) / distSum) * 10) / 10
            : 0;
        const maxSpeed =
          vehicleTrips.length === 0 ? 0 : Math.round(Math.max(...vehicleTrips.map((item) => item.maxSpeed)) * 10) / 10;
        const fuelTotal = Math.round(vehicleTrips.reduce((sum, item) => sum + item.fuelTotal, 0) * 100) / 100;
        const attrs = summarizeRouteAttrs(vehicleTrips).map((summary) => ({
          id: summary.id,
          value: summary.total,
          realValue: summary.total,
          displayValue: summary.displayTotal,
          displayTitle: summary.title,
          unit: summary.unit,
        }));

        // The drivers who actually drove in the period, not the vehicle's current assignment.
        const driverRefs = collectDriverRefs(vehicleTrips);

        // Fuel taken on, as opposed to `fuelTotal`, which is fuel burned.
        const refuelled = summarizeRefuellings(vehicleTrips);

        const row = {
          name: vehicle.name,
          deviceId: vehicle.deviceId,
          plateNumber: vehicle.plateNumber,
          groupName: vehicle.groupName,
          driverRefs,
          totalTrips,
          totalDistance,
          totalDuration,
          avgSpeed,
          maxSpeed,
          fuelTotal,
          refuelCount: refuelled.count,
          attrs,
        };
        const formatted = {
          ...row,
          // Plain text for the PDF and Excel, which read String(row[col.key]); the table cell
          // renders `driverRefs` through DriverLabelList instead.
          drivers: formatDriverDisplaysText(resolveDriverDisplays(driverRefs, driverNamesById)),
          totalDistance: formatMetric(totalDistance, t.common.kilometersShort),
          totalDuration: formatDuration(totalDuration),
          avgSpeed: formatMetric(avgSpeed, t.common.kilometersPerHourShort),
          maxSpeed: formatMetric(maxSpeed, t.common.kilometersPerHourShort),
          fuelTotal: formatMetric(fuelTotal, t.common.litersShort),
          avgConsumption: formatConsumption(fuelTotal, totalDistance, t.common.litersShort),
          refuelledTotal:
            refuelled.litres > 0
              ? formatMetric(refuelled.litres, t.common.litersShort)
              : NO_VALUE,
        };
        const raw: ExcelRawValues = {
          totalDistance,
          totalDuration: totalDurationSeconds,
          avgSpeed,
          maxSpeed,
          fuelTotal: fuelTotal > 0 ? fuelTotal : undefined,
          avgConsumption: consumptionPer100Km(fuelTotal, totalDistance),
          refuelCount: refuelled.count,
          refuelledTotal: refuelled.litres > 0 ? refuelled.litres : undefined,
          ...rawRouteAttrValues(row),
        };
        return {
          ...flattenRouteAttrs(formatted as TripHistory & typeof formatted),
          [EXCEL_RAW]: raw,
        };
      });
    }
    default:
      return [];
  }
}

/**
 * The footer row, keyed by column. Built from the `EXCEL_RAW` twins rather than the formatted
 * cells, so it sums real numbers instead of parsing "29.1 km/h" back apart.
 *
 * Columns without an `aggregate` are absent from the map and render blank.
 */
export function computeReportTotals(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  formatDuration: ReportFormatters["formatDuration"],
): ReportTotals | null {
  if (rows.length === 0) return null;

  const totals: ReportTotals = {};

  for (const col of columns) {
    if (!col.aggregate) continue;

    let sum = 0;
    let weight = 0;
    let max = Number.NEGATIVE_INFINITY;
    let seen = 0;

    for (const row of rows) {
      const value = excelRawValue(row, col.key);
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      seen += 1;
      sum += value;
      if (value > max) max = value;
      if (col.weightKey) {
        const w = excelRawValue(row, col.weightKey);
        if (typeof w === "number" && Number.isFinite(w)) weight += w;
      }
    }

    if (seen === 0) continue;

    let raw: number;
    if (col.aggregate === "max") {
      raw = max;
    } else if (col.aggregate === "avg") {
      if (col.weightKey) {
        // Weighted mean: rebuild the numerator as value x weight, so a long trip counts more.
        let weighted = 0;
        for (const row of rows) {
          const value = excelRawValue(row, col.key);
          const w = excelRawValue(row, col.weightKey);
          if (typeof value !== "number" || !Number.isFinite(value)) continue;
          if (typeof w !== "number" || !Number.isFinite(w)) continue;
          weighted += value * w;
        }
        raw = weight > 0 ? weighted / weight : sum / seen;
      } else {
        raw = sum / seen;
      }
    } else {
      raw = sum;
    }

    const rounded = Math.round(raw * 100) / 100;
    let display: string;
    if (col.formatTotal) {
      display = col.formatTotal(raw);
    } else if (col.excel === "duration") {
      // The raw twin of a duration column is seconds (`durationSeconds`), while the cells above
      // it are rendered from minutes — convert before formatting or the total reads 60x high.
      display = formatDuration(Math.round(rounded / 60));
    } else if (col.unit) {
      display = formatMetric(rounded, col.unit);
    } else {
      display = String(rounded);
    }

    totals[col.key] = { display, raw: rounded };
  }

  return Object.keys(totals).length > 0 ? totals : null;
}

/** The applied filters, spelled out for the PDF header. */
export function reportFilterFields(
  input: {
    range: ReportRange;
    groupFilter: string;
    vehicleFilter: string;
    groups: readonly VehicleGroup[];
    vehicles: readonly Vehicle[];
    rowCount: number;
  },
  t: Translations,
  formatDateTime: ReportFormatters["formatDateTime"],
): PdfExportField[] {
  const { range, groupFilter, vehicleFilter, groups, vehicles } = input;
  const vehicle = vehicles.find((item) => item.id === vehicleFilter);

  return [
    {
      label: t.common.dateRangeLabel,
      value: `${formatDateTime(range.from)} - ${formatDateTime(range.to)}`,
    },
    {
      label: t.historyPage.group,
      value:
        groupFilter === "all"
          ? t.historyPage.allGroups
          : (groups.find((item) => item.id === groupFilter)?.name ?? t.historyPage.allGroups),
    },
    {
      label: t.historyPage.vehicle,
      value: vehicle
        ? [vehicle.name, vehicle.plateNumber].filter(Boolean).join(" · ")
        : t.historyPage.allVehicles,
    },
    { label: t.common.records, value: String(input.rowCount) },
  ];
}

/** The PDF body: display strings, with the totals as a last row labelled in its first cell. */
export function reportPdfRows(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  totals: ReportTotals | null,
  t: Translations,
): string[][] {
  return [
    ...rows.map((row) => columns.map((col) => String(row[col.key] ?? ""))),
    ...(totals
      ? [
          columns.map((col, colIndex) =>
            colIndex === 0 ? t.common.total : (totals[col.key]?.display ?? ""),
          ),
        ]
      : []),
  ];
}

/**
 * Unlike the PDF, Excel gets unformatted values with the unit moved into the header, so the sheet
 * can be sorted, filtered and summed. Only this function reads the raw twins.
 */
export function reportExcelSheet(
  rows: readonly ReportRow[],
  columns: readonly ReportColumn[],
  totals: ReportTotals | null,
  t: Translations,
): { headers: string[]; rows: ExcelCellInput[][] } {
  return {
    headers: columns.map((col) => col.excelLabel ?? withUnit(col.label, col.unit)),
    rows: [
      ...rows.map((row) => columns.map((col) => toExcelCell(excelRawValue(row, col.key), col))),
      // The totals row carries raw numbers, not the formatted strings, so the sheet keeps
      // summing and sorting the column it sits under.
      ...(totals
        ? [
            columns.map((col, colIndex) =>
              colIndex === 0 ? toExcelCell(t.common.total, {}) : toExcelCell(totals[col.key]?.raw, col),
            ),
          ]
        : []),
    ],
  };
}

/** Without extension; `day` is the caller's formatted date. */
export function reportFileName(type: ReportType, day: string): string {
  return `${type}-report-${day}`;
}
